import type { TOC } from '@ember/component/template-only';
import type { ComponentLike } from '@glint/template';

/**
 * Accent for the value text. The original API exposed an open decorative
 * palette (white / emerald / violet / amber / teal / blue / red). Per the
 * 2026-05-27 product decision GlassCard accents fold into the same brand-
 * leaning curated treatment as PageHero — `default` (white, the on-hero
 * baseline) and `brand` (purple-tinted highlight). Status-flavored accents
 * are explicitly NOT in scope: a glass card sits on a decorative gradient,
 * so its accent is a visual highlight, not a status signal.
 */
export type GlassCardAccent = 'default' | 'brand';

interface GlassCardSignature {
  Args: {
    /** Phosphor icon component to display */
    icon?: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Label text displayed above the value */
    label: string;
    /** Main value to display */
    value: string | number;
    /** Secondary description below the value */
    subtext?: string;
    /** Accent variant for the value text (default: 'default'). */
    accent?: GlassCardAccent;
  };
  Blocks: {
    /** Optional rich subtext content (alternative to @subtext arg, for icons/trends) */
    subtext?: [];
    /** Optional extra content below the value area */
    extra?: [];
  };
  Element: HTMLDivElement;
}

/**
 * Resolves an accent to the semantic class defined in
 * `addons/tio-ui/src/styles/`. Both presets use mode-aware tokens
 * so the highlight follows the canonical palette without per-accent dark
 * overrides.
 */
const getAccentClass = (accent: GlassCardAccent = 'default') =>
  accent === 'brand' ? 'tio-glass-value--brand' : 'tio-glass-value--default';

/**
 * GlassCard - A translucent stat card for dark/hero background contexts
 *
 * Designed to sit on gradient backgrounds (e.g., inside a PageHero).
 * Uses the `.tio-glass` frosted-surface recipe
 * with white text and a small brand-leaning accent set for the value.
 *
 * @example Basic usage
 * ```gts
 * import GlassCard from 'tio-ui/components/glass-card';
 *
 * <GlassCard @label="Career Score" @value="720" @subtext="of 1,000 points" />
 * ```
 *
 * @example Inside a PageHero with brand accent
 * ```gts
 * <PageHero @gradient="cool">
 *   <:default>
 *     <div class="grid grid-cols-3 gap-4 mt-6">
 *       <GlassCard @label="Balance" @value="$47,850" @subtext="+12.3% YTD" />
 *       <GlassCard @label="Match" @value="$3,600" @accent="brand" @subtext="Free money" />
 *       <GlassCard @label="Projected" @value="$1.2M" @subtext="At current rate" />
 *     </div>
 *   </:default>
 * </PageHero>
 * ```
 */
const GlassCard: TOC<GlassCardSignature> = <template>
  <div data-test-glass-card class="tio-glass" ...attributes>
    {{#if @icon}}
      <div class="tio-row-tight mb-2">
        {{#let @icon as |Icon|}}
          <Icon @size={{18}} class="tio-glass-label" />
        {{/let}}
        <span data-test-glass-card-label class="tio-glass-label">{{@label}}</span>
      </div>
    {{else}}
      <div data-test-glass-card-label class="tio-glass-label mb-1">{{@label}}</div>
    {{/if}}
    <div data-test-glass-card-value class="tio-glass-value {{getAccentClass @accent}}">
      {{@value}}
    </div>
    {{#if (has-block "subtext")}}
      <div data-test-glass-card-subtext class="tio-row-extra-tight tio-glass-subtext">
        {{yield to="subtext"}}
      </div>
    {{else if @subtext}}
      <div data-test-glass-card-subtext class="tio-glass-subtext">{{@subtext}}</div>
    {{/if}}
    {{#if (has-block "extra")}}
      <div class="mt-2">
        {{yield to="extra"}}
      </div>
    {{/if}}
  </div>
</template>;

export default GlassCard;

export type { GlassCardSignature };
